import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { useDeleteTraeger, useTraegerList } from '@/api/useTraeger';
import { useIsAdmin } from '@/auth/useRequireRole';
import { DoubleConfirmReasonDialog } from '@/components/DoubleConfirmReasonDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { TraegerEditDialog } from '@/pages/traeger/TraegerEditDialog';
import type { Traeger } from '@/api/types';

function describeError(err: unknown): string {
  const ax = err as AxiosError<{ detail?: string | { message?: string } }> | undefined;
  const detail = ax?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail && detail.message) {
    return String(detail.message);
  }
  return ax?.message ?? '';
}

export function TraegerListPage() {
  const { t } = useTranslation(['traeger', 'common', 'errors']);
  const isAdmin = useIsAdmin();
  const list = useTraegerList();
  const deleteMutation = useDeleteTraeger();

  const [editTarget, setEditTarget] = useState<Traeger | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Traeger | null>(null);

  if (!isAdmin) {
    return (
      <Stack>
        <Title order={2}>{t('traeger:title')}</Title>
        <ErrorState title={t('errors:missingRole.title')} error={t('errors:missingRole.description')} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('traeger:title')}</Title>
          <Text c="dimmed">{t('traeger:subtitle')}</Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
          {t('traeger:actions.create')}
        </Button>
      </Group>

      {list.isLoading ? <LoadingState /> : null}
      {list.isError ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
      {list.data && list.data.length === 0 ? (
        <EmptyState
          title={t('traeger:list.empty')}
          description={t('traeger:list.emptyHint')}
          action={
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
              {t('traeger:actions.create')}
            </Button>
          }
        />
      ) : null}

      {list.data && list.data.length > 0 ? (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('traeger:col.name')}</Table.Th>
                  <Table.Th>{t('traeger:col.finanzservice')}</Table.Th>
                  <Table.Th>{t('traeger:col.einrichtungen')}</Table.Th>
                  <Table.Th aria-label={t('traeger:col.aktionen')} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.map((tr) => (
                  <Table.Tr key={tr.id}>
                    <Table.Td>{tr.name}</Table.Td>
                    <Table.Td>
                      <Badge color={tr.finanzservice_intern ? 'green' : 'gray'} variant="light">
                        {tr.finanzservice_intern ? t('common:yes') : t('common:no')}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{tr.einrichtungen_count ?? '—'}</Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Tooltip label={t('traeger:actions.edit')}>
                          <ActionIcon
                            variant="subtle"
                            onClick={() => setEditTarget(tr)}
                            aria-label={t('traeger:actions.edit')}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('traeger:actions.delete')}>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => setDeleteTarget(tr)}
                            aria-label={t('traeger:actions.delete')}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      ) : null}

      <TraegerEditDialog
        opened={createOpen}
        traeger={null}
        onClose={() => setCreateOpen(false)}
      />
      <TraegerEditDialog
        opened={editTarget !== null}
        traeger={editTarget}
        onClose={() => setEditTarget(null)}
      />

      {deleteTarget ? (
        <DoubleConfirmReasonDialog
          opened={!!deleteTarget}
          expected={deleteTarget.name}
          title={t('traeger:delete.title')}
          message={t('traeger:delete.message', { name: deleteTarget.name })}
          reasonLabel={t('traeger:delete.reasonLabel')}
          reasonPlaceholder={t('traeger:delete.reasonPlaceholder')}
          reasonRequiredText={t('traeger:delete.reasonRequired')}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async (reason) => {
            const target = deleteTarget;
            setDeleteTarget(null);
            try {
              await deleteMutation.mutateAsync({
                id: target.id,
                confirm_name: target.name,
                reason,
              });
              notifications.show({ color: 'teal', message: t('traeger:info.deleted') });
            } catch (err) {
              notifications.show({
                color: 'red',
                title: t('errors:generic'),
                message: describeError(err) || t('errors:generic'),
              });
            }
          }}
        />
      ) : null}
    </Stack>
  );
}
